import { useMemo } from 'react';
import { SortingState, PaginationState } from '../../types';

export interface UseDataProcessingParams<T extends Record<string, unknown>> {
  rows: T[];
  searchValue: string;
  sorting?: SortingState<T>;
  pagination?: PaginationState;
}

/**
 * Hook for processing table data with search, sorting, and pagination
 *
 * This hook handles client-side data processing operations including:
 * - Search filtering across all row values
 * - Column sorting (ascending/descending)
 * - Pagination with page-based navigation
 *
 * @template T - The type of data rows being processed
 * @param params - Configuration object containing:
 *   - rows: Array of data rows to process
 *   - searchValue: Search term for filtering
 *   - sorting: Current sorting configuration
 *   - pagination: Current pagination configuration
 *
 * @returns Object containing:
 *   - processedRows: Final processed and paginated rows
 *   - totalRows: Total number of rows after search filtering
 *   - paginatedRows: Rows for the current page
 *
 * @example
 * ```tsx
 * const { processedRows, totalRows } = useDataProcessing({
 *   rows: userData,
 *   searchValue: 'john',
 *   sorting: { field: 'name', direction: 'asc' },
 *   pagination: { page: 0, pageSize: 10 }
 * });
 * ```
 */
export function useDataProcessing<T extends Record<string, unknown>>({
  rows,
  searchValue,
  sorting,
  pagination,
}: UseDataProcessingParams<T>) {
  const processedRows = useMemo(() => {
    let filteredRows = [...rows];

    // Apply quick-filter style search across all columns
    // Split input by spaces or commas into tokens and require ALL tokens to match
    if (searchValue.trim()) {
      const tokens = searchValue
        .split(/[\s,]+/)
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      if (tokens.length > 0) {
        filteredRows = filteredRows.filter((row) =>
          tokens.every((token) =>
            Object.values(row).some((value) =>
              String(value).toLowerCase().includes(token),
            ),
          ),
        );
      }
    }

    // Apply sorting
    if (sorting) {
      filteredRows.sort((a, b) => {
        const aValue = a[sorting.orderBy];
        const bValue = b[sorting.orderBy];
        if (aValue < bValue) return sorting.order === 'asc' ? -1 : 1;
        if (aValue > bValue) return sorting.order === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filteredRows;
  }, [rows, searchValue, sorting]);

  // Apply pagination separately to get the correct slice
  const paginatedRows = useMemo(() => {
    if (!pagination) return processedRows;

    const { page, rowsPerPage } = pagination;
    return processedRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [processedRows, pagination]);

  return {
    processedRows, // All filtered and sorted rows (for pagination count)
    paginatedRows, // Final rows to display (after pagination)
    totalCount: processedRows.length,
  };
}
